<template>
    <div>
        <div class="direct-sign-record container mb-4">
            <div class="mt-3 mb-3"></div>
            
            <!-- RECORDING STEP -->
            <div v-if="step === 1 && isMobile" class="container vgtc-card record-container-padding">
                <SmallDirectRecordSign
                    ref="small"
                    :holistic="holistic"
                    :busy="loading"
                    @nextStep="analyseRecording"
                    @cancel="goBackToSearch" />
            </div>

            <div v-if="step === 1 && !isMobile" class="container vgtc-card record-container-padding">
                <LargeDirectRecordSign
                    ref="large"
                    :holistic="holistic"
                    :busy="loading"
                    @nextStep="analyseRecording"
                    @cancel="goBackToSearch" />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import SmallDirectRecordSign from '@/components/signFeedback/SmallDirectRecordSign.vue';
import LargeDirectRecordSign from '@/components/signFeedback/LargeDirectRecordSign.vue';
import * as Holistic from '@mediapipe/holistic';
import ProcessSignLoading from '@/components/signFeedback/ProcessSignLoading.vue';
import { ISignOverview } from '../../../functions/src/shared/contracts/api/GlossContract';
import { signFeedbackService } from '@/shared/services/SignFeedbackService';
import { v4 as uuidv4 } from 'uuid';

@Component({
    components: {
        ProcessSignLoading,
        SmallDirectRecordSign,
        LargeDirectRecordSign,
    },
})
export default class DirectSignRecord extends Vue {

    public step = 1;
    public feedbackSign: ISignOverview | null = null;
    public loading = false;
    public landMarks = null;
    public signKnowledge: string | null = null;
    public feedbackResult: { gloss: ISignOverview, score: number, feedbackGloss: boolean }[] = null;

    public isMobile = false;

    public holistic = null;

    mounted() {
        this.holistic = new Holistic.Holistic({
            locateFile: (file: string) => {
                return `mediapipe/${file}`;
            },
        });

        this.holistic.setOptions({
            modelComplexity: 2,
            smoothLandmarks: true,
            enableSegmentation: false,
            smoothSegmentation: false,
            refineFaceLandmarks: false,
            minDetectionConfidence: 0.5,
            minTrackingConfidence: 0.5,
        });

        this.onResize();
        window.addEventListener('resize', this.onResize);
        signFeedbackService.sessionId = uuidv4();    
    }

    onResize() {
        this.isMobile = window.innerWidth <= 760;
    }

    public nextStep() {
        this.step++;
        window.scrollTo(0, 0);
    }

    public async analyseRecording(recordingLandmarks: any[]) {
        this.landMarks = recordingLandmarks;
        //this.nextStep();
        this.loading = true;
        signFeedbackService.analyseRecording(recordingLandmarks, 'no')
            .then(result => {
                this.feedbackResult = result;
                const mappedResults = (result || []).map(r => r.gloss);
                this.$store.dispatch('search/setExternalResults', { results: mappedResults, source: 'camera' })
                    .then(() => {
                        this.teardownChildren();
                        this.stopAnyActiveVideoStreams();
                        this.$router.push({ path: '/search', hash: '#cameraResultsTop' });
                        try { this.$bvToast.show('recording-success-toast'); } catch (e) { /* noop */ }
                    });
            })
            .catch(() => this.showErrorToast())
            .finally(() => this.loading = false);
    }

    public goBackToSearch() {
        this.teardownChildren();
        this.stopAnyActiveVideoStreams();
        this.$router.push('/search');
    }
    public showErrorToast() {
        this.$bvToast.show('error-toast');
    }

    public hideErrorToast() {
        this.$bvToast.hide('error-toast');
    }
    
    beforeDestroy() {
        this.teardownChildren();
        if (this.holistic) {
            this.holistic.reset();
            this.holistic.close();
        }
        window.removeEventListener('resize', this.onResize);
    }

    public beforeRouteLeave(to: any, from: any, next: any) {
        this.teardownChildren();
        this.stopAnyActiveVideoStreams();
        if (this.holistic) {
            try { this.holistic.reset(); } catch (e) { /* noop */ }
            try { this.holistic.close(); } catch (e) { /* noop */ }
            // @ts-ignore
            this.holistic = null;
        }
        window.removeEventListener('resize', this.onResize);
        next();
    }

    private teardownChildren() {
        const small: any = (this.$refs as any)['small'];
        if (small && typeof small.teardown === 'function') {
            try { small.teardown(); } catch (e) { /* noop */ }
        } else if (small && small.$refs && small.$refs['stage'] && typeof small.$refs['stage'].teardown === 'function') {
            try { small.$refs['stage'].teardown(); } catch (e) { /* noop */ }
        }
        const large: any = (this.$refs as any)['large'];
        if (large && typeof large.teardown === 'function') {
            try { large.teardown(); } catch (e) { /* noop */ }
        } else if (large && large.$refs && large.$refs['stage'] && typeof large.$refs['stage'].teardown === 'function') {
            try { large.$refs['stage'].teardown(); } catch (e) { /* noop */ }
        }
    }

    private stopAnyActiveVideoStreams() {
        try {
            const videos = Array.from(document.querySelectorAll('video')) as HTMLVideoElement[];
            videos.forEach(v => {
                const stream = (v as any).srcObject as MediaStream | null;
                if (stream && typeof stream.getTracks === 'function') {
                    try { stream.getTracks().forEach(t => { try { t.stop(); } catch (e) { /* noop */ } }); } catch (e) { /* noop */ }
                }
                try { v.pause && v.pause(); } catch (e) { /* noop */ }
                try { (v as any).srcObject = null; } catch (e) { /* noop */ }
            });
        } catch (e) { /* noop */ }
    }
}
</script>

<style lang="scss" scoped>
.direct-sign-record {
    min-height: 80vh;
}

.icon-sign-buddy {
    height: 40px;
}

.record-container-padding {
    padding: 2.5rem 2rem;
}

@media (max-width: 576px) {
    .record-container-padding {
        padding: 1rem;
    }
}
</style>

 